import { NxButtonModule } from "@allianz/ng-aquila/button";
import { NxCopytextModule } from "@allianz/ng-aquila/copytext";
import { NxGridModule } from "@allianz/ng-aquila/grid";
import { NxHeaderModule } from "@allianz/ng-aquila/header";
import { NxIconModule } from "@allianz/ng-aquila/icon";
import { NxLinkModule } from "@allianz/ng-aquila/link";
import { NxMenuModule } from "@allianz/ng-aquila/menu";
import { BreakpointObserver } from "@angular/cdk/layout";
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router, RouterModule } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Store } from "@ngxs/store";
import { AuthSessionFacade } from "@services/auth-session/auth-session.facade";
import { CrossAppNavigationService } from "@services/cross-app-navigation/cross-app-navigation.service";
import { MenuNavigationService } from "@services/menu-navigation/menu-navigation.service";
import { ADMINISTRATOR_MENU_ACCESS } from "@shared/constants/menu-nav-registry.constants";
import { formatLocalDateTime } from "@shared/functions/date-utils";
import { MenuItem } from "@shared/interfaces/menu-item.model";
import { UserResponse } from "@shared/interfaces/user.model";
import { SET_ENQUIRY_SEARCH_CRITERIA } from "@store/customer-management/customer-management.action";
import { GET_NOTIFICATION_LIST } from "@store/notification/notification.action";
import { NotificationState } from "@store/notification/notification.state";
import { LOGOUT, TOGGLE_ADMINISTRATOR_BTN } from "@store/user/user.action";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";

import { NotificationSidebarComponent } from "../notification-sidebar/notification-sidebar.component";

@Component({
  selector: "app-header",
  imports: [
    CommonModule,
    RouterModule,
    NxButtonModule,
    NxCopytextModule,
    NxGridModule,
    NxHeaderModule,
    NxIconModule,
    NxLinkModule,
    NxMenuModule,
    NotificationSidebarComponent,
    TranslateModule,
  ],
  templateUrl: "./header.component.html",
  styleUrl: "./header.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Input() public user: Partial<UserResponse> | null = null;

  @Output() public isShowMenu: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  protected menuPolicyMgmt: any[] = [];
  protected menuList: any[] = [];
  protected menuESubmission: any[] = [];
  protected showESubTemplateMenu = false;

  private readonly eSubmissionTemplateRouteMap: Record<string, string> = {
    SA_ESUB_SUBMISSION: "/e-submission-template/submission",
    SA_ESUB_ENQUIRY: "/e-submission-template/submission-enquiry",
    SA_ESUB_SUMMARY: "/e-submission-template/submission-summary",
    SA_ESUB_OUTSTANDING: "/e-submission-template/outstanding-submission-view",
    SA_ESUB_PYMT_ENQ: "/e-submission-template/payment-enquiry",
    SA_ESUB_AGENT_EINV: "/e-submission-template/agent-e-invoice",
    SA_ESUB_TAX_INV: "/e-submission-template/tax-invoice-statement-listing",
  };

  private userInfo: Partial<UserResponse> | null = null;
  private primedNotificationUserId: string | null = null;
  protected unreadNotification$ = this.store.select(
    NotificationState.countUnreadNotification,
  );

  protected showmenu = true;
  protected screenWidth: number;
  protected isMobileScreen: boolean;
  protected showParentMenu: boolean;
  protected isAdministratorActivated$: Observable<boolean> | null = null;
  protected showDropdown = false;
  protected subMenu: any = null;
  protected adminMenuList: any[] = [];
  protected showAdminMenu = false;
  protected showESubMenu = false;

  protected isNotificationClicked = false;

  private readonly eSubmissionRouteMap: Record<string, string> = {
    SA_ESUB_SUBMISSION: "/e-submission/submissions",
    SA_ESUB_ENQUIRY: "/e-submission/submission-enquiry",
    SA_ESUB_SUMMARY: "/e-submission/submission-summary",
    SA_ESUB_OUTSTANDING: "/e-submission/submission-outstanding-view",
    SA_ESUB_PYMT_ENQ: "/e-submission/payment-enquiry",
    SA_ESUB_AGENT_EINV: "/e-submission/agent-e-invoice",
    SA_ESUB_TAX_INV: "/e-submission/tax-invoice-statement-listing",
  };

  protected get userName$() {
    const preferName =
      this.user?.preferName ??
      this.userInfo?.preferName ??
      this.userInfo?.name ??
      "User";
    return this.translate
      .get("HEADER.WELCOME")
      .pipe(map((text: string) => `${text}, ${preferName}`));
  }

  protected get lastLogin$() {
    const lastLoginDate =
      this.user?.lastLoginDate ?? this.userInfo?.lastLoginDate ?? null;
    const formattedDate = formatLocalDateTime(lastLoginDate);
    return this.translate
      .get("HEADER.LAST_LOGIN_TIME")
      .pipe(map((text: string) => `${text}: ${formattedDate}`));
  }

  protected get isAdmin$() {
    if (this.userInfo?.menuAccess) {
      if (this.userInfo.menuAccess.includes(ADMINISTRATOR_MENU_ACCESS)) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  protected get adminMenu() {
    return this.adminMenuList;
  }

  constructor(
    private readonly translate: TranslateService,
    private readonly store: Store,
    private readonly authSessionFacade: AuthSessionFacade,
    private readonly elementRef: ElementRef,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly crossAppNavigationService: CrossAppNavigationService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef,
    private readonly menuNav: MenuNavigationService,
  ) {
    this.screenWidth = window.innerWidth;
    // this.showmenu = this.screenWidth >= 993;
  }

  ngOnInit() {
    this.showParentMenu = false;
    this.showESubMenu = false;

    this.userInfo = this.authSessionFacade.userSnapshot;
    this.setAdminList();

    const snapshotUserId = this.authSessionFacade.userSnapshot?.userId;
    if (snapshotUserId) {
      this.primeUnreadNotificationCount(snapshotUserId);
    }

    this.isAdministratorActivated$ = this.store.select(
      (state: any) => state.UserState?.isAdministratorActivated,
    );

    this.authSessionFacade.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.userInfo = user;
        this.setAdminList();
        if (user?.userId) {
          this.primeUnreadNotificationCount(user.userId);
        }
        this.cdRef.markForCheck();
      });

    this.authSessionFacade.policyMenu$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((menu: any) => {
        const subMenu = menu?.subMenu ?? [];
        this.menuPolicyMgmt = JSON.parse(JSON.stringify(subMenu));
        this.cdRef.markForCheck();
      });

    this.authSessionFacade.menuList$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((menuList: any[]) => {
        const clonedMenuList = JSON.parse(JSON.stringify(menuList ?? []));
        this.menuList = this.sortTopLevelMenus(clonedMenuList);
        const eSubEntry = clonedMenuList.find(
          (m: any) => m.menuId === "MNESUBMISSION",
        );
        this.menuESubmission = eSubEntry?.subMenu ?? [];
        this.cdRef.markForCheck();
      });

    this.breakpointObserver
      .observe(["(max-width: 993px)"])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.isMobileScreen = result.matches;
        this.showmenu = !result.matches;
        this.cdRef.markForCheck();
      });
  }

  // @HostListener("window:resize", ["$event"])
  // onResize(event: Event) {
  //   this.screenWidth = window.innerWidth;
  //   this.showmenu = this.screenWidth >= 993;
  // }

  private checkScreen() {
    const screenWidth = window.innerWidth;
    if (screenWidth < 993) {
      this.showmenu = false;
    }
  }

  protected toggleMenu() {
    this.showmenu = !this.showmenu;
  }

  protected onHoverToggleParentMenu(isOpen: boolean) {
    this.showParentMenu = isOpen;
    this.showAdminMenu = false;
    this.showESubMenu = false;
    this.isNotificationClicked = false;
  }

  protected onHoverToggleNonPolicy(isOpen: boolean) {
    this.showParentMenu = isOpen;
    this.showAdminMenu = false;
    this.showESubMenu = false;
  }

  protected toggleParentMenu() {
    this.showParentMenu = !this.showParentMenu;
    this.showAdminMenu = false;
    this.showESubMenu = false;
    this.isNotificationClicked = false;
  }

  protected onHoverToggleAdminMenu(isOpen: boolean) {
    this.showAdminMenu = isOpen;
    this.showParentMenu = false;
    this.showESubMenu = false;
    this.isNotificationClicked = false;
  }

  protected toggleAdminMenu() {
    this.showAdminMenu = !this.showAdminMenu;
    this.showParentMenu = false;
    this.showESubMenu = false;
    this.isNotificationClicked = false;
  }

  protected onHoverToggleESubMenu(isOpen: boolean) {
    this.showESubMenu = isOpen;
    this.showParentMenu = false;
    this.showAdminMenu = false;
  }

  protected toggleESubMenu() {
    this.showESubMenu = !this.showESubMenu;
    this.showParentMenu = false;
    this.showAdminMenu = false;
    this.isNotificationClicked = false;
    this.showESubTemplateMenu = false;
  }

  protected onHoverToggleESubTemplateMenu(isOpen: boolean) {
    this.showESubTemplateMenu = isOpen;
    this.showParentMenu = false;
    this.showAdminMenu = false;
    this.showESubMenu = false;
  }

  protected toggleESubTemplateMenu() {
    this.showESubTemplateMenu = !this.showESubTemplateMenu;
    this.showParentMenu = false;
    this.showAdminMenu = false;
    this.isNotificationClicked = false;
    this.showESubMenu = false;
  }

  protected navToESubmissionTemplate(item: any): void {
    const route = this.eSubmissionTemplateRouteMap[item.menuId];
    if (route) {
      this.showESubTemplateMenu = false;
      this.router.navigateByUrl(route);
    }
  }

  protected onClick(item: any) {
    item.hidden = !item.hidden;
  }

  protected nav(linkOrItem: string | MenuItem, item?: MenuItem) {
    const targetItem =
      item ?? (typeof linkOrItem === "object" ? linkOrItem : undefined);

    if (!targetItem) {
      return;
    }

    targetItem.expanded = !targetItem.expanded;
    this.checkScreen();
    this.menuNav.navigate(targetItem);
  }

  protected open(submenu: MenuItem, parentMenu: any, menu: any) {
    this.checkScreen();
    submenu.expanded = !submenu.expanded;
    parentMenu.expanded = !parentMenu.expanded;
    menu.expanded = !menu.expanded;
    this.menuNav.navigate(submenu);
  }

  protected openURL(url, name, spec) {
    window.open(url, name, spec);
  }

  protected switchURL(url: string) {
    if (
      typeof url === "string" &&
      url.trim().toUpperCase() === "SAT_ADMIN_MENU"
    ) {
      this.navigateToAdminMenu();
      return;
    }
    this.menuNav.navigateByKey(url);
  }

  /**
   * The administrator module is guarded by AdministratorGuard, which requires
   * UserState.isAdministratorActivated. Toggle it on (once) before entering.
   */
  private navigateToAdminMenu(): void {
    const isActivated = this.store.selectSnapshot<boolean>(
      (state: any) => state.UserState?.isAdministratorActivated,
    );
    if (isActivated) {
      void this.router.navigateByUrl("/administrator/login-alert");
      return;
    }
    this.store
      .dispatch(new TOGGLE_ADMINISTRATOR_BTN())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        () => void this.router.navigateByUrl("/administrator/login-alert"),
      );
  }

  protected administarorToggled(): void {
    this.store
      .dispatch(new TOGGLE_ADMINISTRATOR_BTN())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: any) => {
        if (res?.UserState?.isAdministratorActivated) {
          void this.router.navigateByUrl("/administrator/login-alert");
        } else {
          void this.router.navigateByUrl("/home");
        }
      });
  }

  protected onAdminSubmenuClick(route: string): void {
    const isActivated = this.store.selectSnapshot<boolean>(
      (state: any) => state.UserState?.isAdministratorActivated,
    );

    if (isActivated) {
      void this.router.navigateByUrl(route);
      return;
    }

    this.store
      .dispatch(new TOGGLE_ADMINISTRATOR_BTN())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.router.navigateByUrl(route));
  }

  protected clearSearch(): void {
    this.store.dispatch(new SET_ENQUIRY_SEARCH_CRITERIA(null));
  }

  protected closeMenuMobile(): void {
    this.showParentMenu = false;
    this.showAdminMenu = false;
    if (this.isMobileScreen) {
      this.showmenu = false;
    }
  }

  protected closeToggleMenuMobile(): void {
    this.closeMenuMobile();
  }

  protected closeELibrarySubmenu(_item: any): void {
    // no-op compatibility method retained for old header template bindings
  }

  protected resetState(menu: any): void {
    this.store.dispatch(new SET_ENQUIRY_SEARCH_CRITERIA(null));
    this.showDropdown = menu.subMenu ? !this.showDropdown : false;
    this.subMenu = menu.subMenu;
  }

  protected setAdminList(): void {
    const menuAccess = this.userInfo?.menuAccess ?? [];
    const adminList: Array<{ label: string; link: string }> = [
      {
        label: "Login Alert",
        link: "/administrator/login-alert",
      },
      {
        label: "Emergency Alert",
        link: "/administrator/emergency-alert",
      },
    ];

    if (menuAccess.includes("MNADMCAMP")) {
      adminList.push({ label: "Campaigns", link: "/administrator/campaign" });
    }
    if (menuAccess.includes("MNADMEVENT")) {
      adminList.push({ label: "Events", link: "/administrator/events" });
    }
    if (menuAccess.includes("MNADMCSELL")) {
      adminList.push({
        label: "Cross Sell",
        link: "/administrator/cross-sell",
      });
    }

    this.adminMenuList = adminList;
  }

  private primeUnreadNotificationCount(userId: string): void {
    if (this.primedNotificationUserId === userId) {
      return;
    }
    this.primedNotificationUserId = userId;

    const payload = { userId, page: 0, size: 10 };
    this.store
      .dispatch(new GET_NOTIFICATION_LIST(payload, false))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  protected switchToAlpha() {
    if (this.userInfo?.userId) {
      this.crossAppNavigationService.redirect(environment.alpha_url);
    }
  }

  protected signOut() {
    this.store
      .dispatch(new LOGOUT())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  protected isESubCol1(menuId: string): boolean {
    return [
      "SA_ESUB_SUBMISSION",
      "SA_ESUB_ENQUIRY",
      "SA_ESUB_SUMMARY",
      "SA_ESUB_OUTSTANDING",
    ].includes(menuId);
  }

  protected isESubCol2(menuId: string): boolean {
    return ["SA_ESUB_PYMT_ENQ"].includes(menuId);
  }

  protected isESubCol3(menuId: string): boolean {
    return [
      "SA_ESUB_INVOICE_HDR",
      "SA_ESUB_AGENT_EINV",
      "SA_ESUB_TAX_INV",
    ].includes(menuId);
  }

  protected navToESubmission(item: any): void {
    const route = this.eSubmissionRouteMap[item.menuId];
    if (route) {
      this.showESubMenu = false;
      this.router.navigateByUrl(route);
    }
  }

  private sortTopLevelMenus(menuList: any[]): any[] {
    const preferredOrder = new Map<string, number>([
      ["MNDASHBRD", 1],
      ["SA_MNPOLICIES", 2],
      ["MNSALESMNGT", 3],
      ["MNCUSTMNGT", 4],
      ["MNELIBRARY", 5],
      ["MNESUBMISSION", 6],
    ]);

    return (menuList ?? [])
      .map((item: any, index: number) => ({ item, index }))
      .sort((a, b) => {
        const aRank = preferredOrder.get(a.item?.menuId ?? "") ?? 999;
        const bRank = preferredOrder.get(b.item?.menuId ?? "") ?? 999;

        if (aRank !== bRank) {
          return aRank - bRank;
        }

        return a.index - b.index;
      })
      .map(({ item }) => item);
  }

  protected isUnifiedMenu(menuId: any) {
    return [
      "SA_POLSUMMARY",
      "SA_POLREPORTS",
      "SA_POLENQUIRY",
      "SA_POLCI",
    ].includes(menuId);
  }

  protected isMotorMenu(menuId: any) {
    return ["SAT_MT_MOTOR"].includes(menuId);
  }

  protected isNonMotorMenu(menuId: any) {
    return ["SA_NONMOTOR"].includes(menuId);
  }

  protected toggleNotification(event: any) {
    event.stopPropagation();
    if (this.isNotificationClicked) {
      this.isNotificationClicked = false;
      this.elementRef.nativeElement.ownerDocument.body.style.position = null;
      this.elementRef.nativeElement.ownerDocument.body.style.overflow = null;
    } else {
      this.isNotificationClicked = true;
      // Lock background scrolling on small screens when notifications open
      if (window.innerWidth < 993) {
        this.elementRef.nativeElement.ownerDocument.body.style.overflow =
          "hidden";
      }
      // update caret position once the panel is rendered
      setTimeout(() => this.updateNotificationCaretPosition(), 0);
    }
    this.showParentMenu = false;
    this.showAdminMenu = false;
  }

  protected toggledNotification($event: any) {
    this.isNotificationClicked = !!$event;
    if (!this.isNotificationClicked) {
      this.elementRef.nativeElement.ownerDocument.body.style.position = null;
      this.elementRef.nativeElement.ownerDocument.body.style.overflow = null;
    }
  }

  @HostListener("window:resize", ["$event"])
  protected onWindowResize(_event: Event) {
    if (this.isNotificationClicked) {
      this.updateNotificationCaretPosition();
    }
  }

  /**
   * Compute and set the horizontal position of the notification caret (triangle) so it
   * visually centers under the notification trigger icon.
   *
   * How it works:
   *  - Finds the trigger element (.notif-icon) and the popup panel (.header-notification__float-block).
   *  - Uses getBoundingClientRect() to get absolute positions for both elements.
   *  - Computes the caret's left offset relative to the popup: center of trigger minus half the caret width.
   *  - Writes the computed value to the CSS custom property --notif-caret-left on the popup (in pixels).
   *
   * Why a CSS variable:
   *  - Allows the caret pseudo-element in SCSS to read the value and position itself without DOM style hacks.
   *  - Keeps positioning responsive when the popup is re-rendered or resized.
   **/
  private updateNotificationCaretPosition(): void {
    try {
      const host: HTMLElement = this.elementRef.nativeElement;
      const trigger: HTMLElement | null = host.querySelector(
        ".notification-popover .notif-icon",
      );
      const popup: HTMLElement | null = host.querySelector(
        ".notification-popover .notification-panel .header-notification__float-block",
      );

      if (!trigger || !popup) {
        return;
      }

      const triggerRect = trigger.getBoundingClientRect();
      const popupRect = popup.getBoundingClientRect();

      // caret is 12px wide, center it under the trigger
      const caretHalf = 6;
      const caretLeft = Math.round(
        triggerRect.left + triggerRect.width / 2 - popupRect.left - caretHalf,
      );

      popup.style.setProperty("--notif-caret-left", `${caretLeft}px`);
    } catch (_error) {
      // ignore errors; positioning is best-effort
    }
  }

  protected getNotificationIcon(
    unreadCount: number | null | undefined,
  ): string {
    const hasUnread = (unreadCount ?? 0) > 0;

    if (this.isNotificationClicked) {
      return hasUnread
        ? "assets/img/Icon - Notification - New - Active.svg"
        : "assets/img/Icon - Notification - Read - Active.svg";
    }

    return hasUnread
      ? "assets/img/Icon - Notification - New - Inactive.svg"
      : "assets/img/Icon - Notification - Read - Inactive.svg";
  }

  private redirectToDashboard() {
    this.crossAppNavigationService
      .resolveSatSwitchSite("SAT_SYS_URL")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  protected redirectToSummary() {
    this.switchURL("SUMMARY_SYS_URL");
  }
  protected navigateToProfile() {
    this.store.dispatch(new SET_ENQUIRY_SEARCH_CRITERIA(null));
    void this.router.navigateByUrl("/profile");
  }
}
